import type {
  GroupResolution,
  LooseEndDecision,
  LooseEndSet,
  RenameEntry,
  WizardData,
} from '../types';

import { personalKey } from './deriveWizardData';

const norm = (s: string): string => s.trim();

/**
 * The per-clinician rename digest: every (provider, old name → new name)
 * produced by the admin's decisions across all lanes — quick wins,
 * duplicates, personal copies, loose ends. The communication artifact the
 * admin pastes to each clinician after applying.
 */
export const computeRenameDigest = (args: {
  /** Filtered wizard data (quick-win rows already removed from lanes). */
  data: WizardData;
  resolutions: Readonly<Record<string, GroupResolution>>;
  quickWinSets: LooseEndSet[];
  quickWinDecisions: Readonly<Record<string, LooseEndDecision>>;
  looseEndSets: LooseEndSet[];
  looseEndDecisions: Readonly<Record<string, LooseEndDecision>>;
  /** Set-aside outlier renames (already resolved to entries). */
  extraRenames?: RenameEntry[];
}): RenameEntry[] => {
  // Merges chain: a quick-win/duplicate keeper can itself be merged away at
  // loose ends. Build the loser→keeper graph so every type resolves to the
  // FINAL surviving name — a provider must be told the name they'll actually
  // see in the picker, not an intermediate keeper that no longer exists.
  const mergeInto = new Map<string, string>(); // loserId → immediate keeperId
  const keeperName = new Map<string, string>(); // keeperId → its declared name
  // Original type identity (real name never comes from a projected set entry).
  const participants: Array<{
    id: string;
    provider: string | null;
    name: string;
  }> = [];

  const addSets = (
    sets: LooseEndSet[],
    decisions: Readonly<Record<string, LooseEndDecision>>
  ): void => {
    for (const set of sets) {
      const decision = decisions[set.key];
      if (!decision?.merge || decision.archive) continue;
      const keeper =
        set.entries.find((e) => norm(e.name) === norm(decision.keepName))
          ?.version ?? set.entries[0].version;
      keeperName.set(keeper.id, norm(decision.keepName));
      for (const e of set.entries) {
        participants.push({
          id: e.version.id,
          provider: e.version.provider,
          name: e.version.name,
        });
        if (e.version.id !== keeper.id) mergeInto.set(e.version.id, keeper.id);
      }
    }
  };
  addSets(args.quickWinSets, args.quickWinDecisions);
  addSets(args.looseEndSets, args.looseEndDecisions);

  const addLanes = (
    groups: WizardData['divergentGroups'],
    keyOf: (nameKey: string) => string
  ): void => {
    for (const group of groups) {
      const resolution = args.resolutions[keyOf(group.nameKey)];
      if (resolution?.kind !== 'make_shared') continue;
      const winner = group.versions.find((v) => v.id === resolution.winnerId);
      if (!winner) continue;
      keeperName.set(
        winner.id,
        resolution.sharedName?.trim() || norm(group.displayName)
      );
      const setAside = new Set(resolution.setAsideIds ?? []);
      for (const v of group.versions) {
        if (setAside.has(v.id)) continue; // renamed via the set-aside queue
        participants.push({ id: v.id, provider: v.provider, name: v.name });
        if (v.id !== winner.id) mergeInto.set(v.id, winner.id);
      }
    }
  };
  addLanes(args.data.divergentGroups, (k) => k);
  addLanes(args.data.personalClusters, personalKey);

  const resolveFinal = (id: string): string | undefined => {
    const seen = new Set<string>();
    let cur = id;
    while (mergeInto.has(cur) && !seen.has(cur)) {
      seen.add(cur);
      cur = mergeInto.get(cur) as string;
    }
    return keeperName.get(cur);
  };

  const entries: RenameEntry[] = [...(args.extraRenames ?? [])];
  for (const p of participants) {
    if (!p.provider) continue;
    const finalName = resolveFinal(p.id) ?? p.name;
    if (norm(p.name) !== norm(finalName)) {
      entries.push({ provider: p.provider, oldName: p.name, newName: finalName });
    }
  }

  // Dedupe (a version can't rename twice, but identical lines can arise).
  const dedup = new Map<string, RenameEntry>();
  for (const e of entries) {
    dedup.set(`${e.provider}|${norm(e.oldName)}|${norm(e.newName)}`, e);
  }
  return [...dedup.values()].sort(
    (a, b) =>
      (a.provider ?? '').localeCompare(b.provider ?? '') ||
      a.oldName.localeCompare(b.oldName)
  );
};

/** The copy-pasteable per-clinician message. */
export const renameMessageFor = (
  provider: string,
  renames: RenameEntry[]
): string => {
  const lines = renames
    .filter((r) => r.provider === provider)
    .map((r) => `• "${r.oldName}" is now "${r.newName}"`);
  return (
    `Hi ${provider} — as part of our appointment-types cleanup, some of ` +
    `your types were consolidated into shared practice types:\n${lines.join('\n')}\n` +
    `Nothing changes about past notes or your per-visit options.`
  );
};
